import { memo, useEffect, useRef, useState } from 'react';
import VideoChat, { IncomingVideoCallModal } from '../common/VideoChat';
import Draggable from 'react-draggable';
import { createPortal } from 'react-dom';
import ROUTES from '../../constants/routes.js';
import useEventListener from 'use-event-listeners';
import { useRootMemoSelector } from 'use-redux-states';
import './livevideo.css';

// Custom hook to get the current pathname without useLocation
const useCurrentPathname = () => {
  const [pathname, setPathname] = useState(window.location.pathname);

  useEffect(() => {
    const handleLocationChange = () => {
      setPathname(window.location.pathname);
    };

    // Listen for popstate events (browser back/forward)
    window.addEventListener('popstate', handleLocationChange);

    // Create an observer to detect navigation changes
    const observer = new MutationObserver(() => {
      if (pathname !== window.location.pathname) {
        setPathname(window.location.pathname);
      }
    });

    // Observe changes to the document body
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      observer.disconnect();
    };
  }, [pathname]);

  return pathname;
};

const LiveVideo = memo(() => {
  const [div, setDiv] = useState(null);
  const mainDivRef = useRef(null);
  const pathname = useCurrentPathname();

  // Resize state
  const [width, setWidth] = useState(450);
  const [height, setHeight] = useState(250);
  const nodeRef = useRef<HTMLDivElement>(null);
  const resizeStartRef = useRef<{
    pointerId: number;
    x: number;
    y: number;
    width: number;
    height: number;
    maxWidth: number;
    maxHeight: number;
  } | null>(null);

  const conversation_id = useRootMemoSelector('call.conversationId');
  const callOtherUserId = useRootMemoSelector('call.otherUserId');
  const isIncoming = useRootMemoSelector('call.isIncoming');
  const active = !!isIncoming || !!callOtherUserId;

  const getResizeLimits = () => {
    const rect = nodeRef.current?.getBoundingClientRect();
    return {
      maxWidth: Math.max(1, Math.min(window.innerWidth - 16, width + (rect?.left ?? 0) - 16)),
      maxHeight: Math.max(1, Math.min(window.innerHeight - 16, height + (rect?.top ?? 0) - 16))
    };
  };

  const clampSize = (value: number, minimum: number, maximum: number) =>
    Math.min(Math.max(value, Math.min(minimum, maximum)), maximum);

  const handleResizeStart = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    resizeStartRef.current = {
      pointerId: e.pointerId,
      x: e.clientX,
      y: e.clientY,
      width,
      height,
      ...getResizeLimits()
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handleResizeMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const start = resizeStartRef.current;
    if (!start || start.pointerId !== e.pointerId) return;
    setWidth(clampSize(start.width + start.x - e.clientX, 380, start.maxWidth));
    setHeight(clampSize(start.height + start.y - e.clientY, 220, start.maxHeight));
  };

  const handleResizeEnd = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (resizeStartRef.current?.pointerId !== e.pointerId) return;
    resizeStartRef.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
  };

  const handleResizeKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    const step = e.shiftKey ? 32 : 16;
    const { maxWidth, maxHeight } = getResizeLimits();
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      setWidth(clampSize(width + (e.key === 'ArrowLeft' ? step : -step), 380, maxWidth));
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      setHeight(clampSize(height + (e.key === 'ArrowUp' ? step : -step), 220, maxHeight));
    }
  };

  useEffect(() => {
    const fitToViewport = () => {
      setWidth((current) => Math.min(current, Math.max(1, window.innerWidth - 32)));
      setHeight((current) => Math.min(current, Math.max(1, window.innerHeight - 32)));
    };
    fitToViewport();
    window.addEventListener('resize', fitToViewport);
    return () => window.removeEventListener('resize', fitToViewport);
  }, []);

  useEffect(() => {
    if (active && conversation_id) {
      const actions = document.querySelector('#video-call-actions');
      const vCont = document.querySelector('#video-call-container');

      // @ts-expect-error
      const mouseover = () => actions && (actions.style.display = 'flex');

      // @ts-expect-error
      const mouseleave = () => actions && (actions.style.display = 'none');

      vCont?.addEventListener('mouseover', mouseover);

      vCont?.addEventListener('mouseleave', mouseleave);

      return () => {
        vCont?.removeEventListener('mouseover', mouseover);
        vCont?.removeEventListener('mouseleave', mouseleave);
      };
    }
  }, [active, conversation_id, div]);

  useEventListener({ listeners: { 'conversation-mounted': (div) => setDiv(div) } });

  // Legacy PeerCall hooks removed. peers-caller manages lifecycle inside VideoChat.

  const isOnConversationPage = pathname === ROUTES.CONVERSATIONS.pathname;
  const isOnEsignaturePage = pathname.startsWith('/esignature');
  const callContainerNode = div && isOnConversationPage ? div : mainDivRef.current;

  useEffect(() => {
    !isOnConversationPage && setDiv(null);
  }, [isOnConversationPage]);

  return (
    <div ref={mainDivRef}>
      {callContainerNode &&
        active &&
        createPortal(
          <div
            className={isOnConversationPage ? undefined : 'fixed bottom-4 right-4'}
            style={isOnEsignaturePage ? { pointerEvents: 'none' } : undefined}>
            <Draggable nodeRef={nodeRef} cancel='button'>
              <div
                ref={nodeRef}
                style={{
                  ...{ pointerEvents: isOnEsignaturePage ? 'auto' : undefined },
                  width: isOnConversationPage ? undefined : `${width}px`,
                  height: isOnConversationPage ? undefined : `${height}px`
                }}
                className='live-video-window relative'>
                {active && (
                  <VideoChat
                    conversation_id={conversation_id}
                    floating={!isOnConversationPage}
                    style={{ height: `${height}px`, overflow: 'hidden' }}
                  />
                )}
                {isIncoming && <IncomingVideoCallModal isIncoming={isIncoming} />}
                {!isOnConversationPage && (
                  <button
                    type='button'
                    className='live-video-resize-handle absolute left-0 top-0 z-50 flex items-center justify-center border-0 bg-transparent p-0 text-white cursor-nwse-resize touch-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white'
                    style={{ width: 28, height: 28, transform: 'translate(-25%, -25%)' }}
                    aria-label='Resize video window'
                    title='Drag to resize, or use arrow keys'
                    onPointerDown={handleResizeStart}
                    onPointerMove={handleResizeMove}
                    onPointerUp={handleResizeEnd}
                    onPointerCancel={handleResizeEnd}
                    onLostPointerCapture={() => {
                      resizeStartRef.current = null;
                    }}
                    onKeyDown={handleResizeKeyDown}>
                    <span
                      className='flex items-center justify-center text-white shadow-sm'
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: 5,
                        border: '1px solid rgba(255, 255, 255, 0.7)',
                        background: 'rgba(0, 0, 0, 0.6)'
                      }}>
                      <svg width='11' height='11' viewBox='0 0 16 16' fill='none' aria-hidden='true'>
                        <path
                          d='M3 7V3h4M3 3l10 10m-4 0h4V9'
                          stroke='currentColor'
                          strokeWidth='1.5'
                          strokeLinecap='round'
                          strokeLinejoin='round'
                        />
                      </svg>
                    </span>
                  </button>
                )}
              </div>
            </Draggable>
          </div>,
          callContainerNode
        )}
    </div>
  );
});

export default LiveVideo;
